
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Loader2 } from "lucide-react";

interface SignatureSettings {
  signature: string;
}

export const SignatureSettings = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [currentSignature, setCurrentSignature] = useState("⟡⟡  Spark Quotes  ⟡⟡");

  const form = useForm<SignatureSettings>({
    defaultValues: {
      signature: currentSignature,
    },
  });

  // Fetch the current signature when component mounts
  useEffect(() => {
    const fetchSignature = async () => {
      setIsFetching(true);
      try {
        const { data, error } = await supabase
          .from("admin_settings")
          .select("setting_value")
          .eq("setting_key", "signature")
          .maybeSingle(); // Use maybeSingle instead of single to handle empty results

        if (error && error.code !== "PGRST116") {
          console.error("Error fetching signature:", error);
          return;
        }

        if (data) {
          // Parse the JSON value properly, ensuring we handle different formats
          let signature = "⟡⟡  Spark Quotes  ⟡⟡";
          
          if (data.setting_value && typeof data.setting_value === 'object') {
            // If it's an object, check if it has a text property
            const valueObj = data.setting_value as Record<string, any>;
            if (valueObj.text) {
              signature = valueObj.text;
            }
          } else if (typeof data.setting_value === 'string') {
            // If it's directly a string
            signature = data.setting_value;
          }
          
          setCurrentSignature(signature);
          form.setValue("signature", signature);
        }
      } catch (error) {
        console.error("Error fetching signature:", error);
      } finally {
        setIsFetching(false);
      }
    };

    fetchSignature();
  }, [form]);

  const onSubmit = async (data: SignatureSettings) => {
    setIsLoading(true);
    
    try {
      // Check if setting already exists
      const { data: existingData, error: checkError } = await supabase
        .from("admin_settings")
        .select("id")
        .eq("setting_key", "signature")
        .maybeSingle();

      if (checkError && checkError.code !== "PGRST116") {
        throw checkError;
      }

      let updateError;
      
      if (existingData) {
        // Update existing setting
        const { error } = await supabase
          .from("admin_settings")
          .update({
            setting_value: data.signature,
            updated_at: new Date().toISOString(),
          })
          .eq("setting_key", "signature");
        
        updateError = error;
      } else {
        // Insert new setting
        const { error } = await supabase
          .from("admin_settings")
          .insert({
            setting_key: "signature",
            setting_value: data.signature,
            description: "SMS signature for quotes",
          });
        
        updateError = error;
      }

      if (updateError) {
        throw updateError;
      }

      // Update local state
      setCurrentSignature(data.signature);

      toast({
        title: "Signature updated",
        description: "The SMS signature has been updated successfully.",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Failed to update signature",
        description: error.message || "An error occurred while updating the signature.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Quote Signature</CardTitle>
        <CardDescription>
          Set the signature that appears at the end of every quote message sent to subscribers.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isFetching ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="signature"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Signature Text</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter signature text" {...field} />
                    </FormControl>
                    <FormDescription>
                      This text will appear at the end of every quote message.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Saving..." : "Save Changes"}
              </Button>
            </form>
          </Form>
        )}
      </CardContent>
      <CardFooter className="border-t bg-slate-50">
        <div className="text-sm text-muted-foreground">
          <span className="font-semibold">Current signature:</span> {currentSignature}
        </div>
      </CardFooter>
    </Card>
  );
};
